import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useProcessingOverlay } from "@hooks/useProcessingOverlay";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { ReactComponent as IconQuestion } from "@assets/Icons/Icon_Question.svg";
import {
    fetchSubscriptions,
    fetchDevices,
    fetchInterfaces,
    createCluster,
} from "../request/request";

interface CreateClusterProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

interface SubscriptionItem {
    id: number;
    purchaseOrden: string;
}

interface DeviceItem {
    id: string;
    name: string;
    description: string;
}

interface IpItem {
    name: string;
    value: string;
}

export function CreateCluster({ isOpen, onClose, onSuccess }: CreateClusterProps) {
    const { getAccessTokenSilently } = useAuth0();

    // Catálogo de suscripciones
    const [subscriptionList, setSubscriptionList] = useState<SubscriptionItem[]>([]);
    const [selectedSubscription, setSelectedSubscription] = useState("");

    // Lista global de devices disponibles
    const [deviceList, setDeviceList] = useState<DeviceItem[]>([]);

    // IP lists separadas para R1 y R2
    const [ipListR1, setIpListR1] = useState<IpItem[]>([]);
    const [ipListR2, setIpListR2] = useState<IpItem[]>([]);

    // Datos básicos del cluster
    const [clusterName, setClusterName] = useState("");
    const [clusterDescription, setClusterDescription] = useState("");

    // R1
    const [deviceR1, setDeviceR1] = useState("");
    const [priorityR1, setPriorityR1] = useState("");
    const [selectedIpR1, setSelectedIpR1] = useState("");

    // R2
    const [deviceR2, setDeviceR2] = useState("");
    const [priorityR2, setPriorityR2] = useState("");
    const [selectedIpR2, setSelectedIpR2] = useState("");

    // Para mostrar explicaciones "Priority Explanation"
    const [showPriorityR1Info, setShowPriorityR1Info] = useState(false);
    const [showPriorityR2Info, setShowPriorityR2Info] = useState(false);

    const [isFetching, setIsFetching] = useState(false);
    const overlaySpinner = useProcessingOverlay(isFetching);

    useEffect(() => {
        if (!isOpen) return;
        loadSubscriptions();
    }, [isOpen]);

    async function loadSubscriptions() {
        try {
            setIsFetching(true);
            const token = await getAccessTokenSilently();
            const resp = await fetchSubscriptions(token);
            setSubscriptionList(resp.data);
        } catch (error) {
            console.error("Error fetching subscriptions =>", error);
        } finally {
            setIsFetching(false);
        }
    }

    // Al seleccionar suscripción => traemos devices
    async function handleSelectSubscription(name: string) {
        setSelectedSubscription(name);
        resetAllFieldsExceptSubscription();

        const sub = subscriptionList.find((s) => s.purchaseOrden === name);
        if (!sub) return;

        try {
            setIsFetching(true);
            const token = await getAccessTokenSilently();
            const resp = await fetchDevices(token, sub.id);
            setDeviceList(resp.data);
        } catch (error) {
            console.error("Error fetching devices =>", error);
        } finally {
            setIsFetching(false);
        }
    }

    // Función para resetear campos cuando cambia la suscripción
    function resetAllFieldsExceptSubscription() {
        setDeviceList([]);
        setIpListR1([]);
        setIpListR2([]);
        setDeviceR1("");
        setDeviceR2("");
        setSelectedIpR1("");
        setSelectedIpR2("");
        setPriorityR1("");
        setPriorityR2("");
        setClusterName("");
        setClusterDescription("");
    }

    // Device R1
    async function handleSelectDeviceR1(deviceName: string) {
        setDeviceR1(deviceName);
        setIpListR1([]);
        setSelectedIpR1("");
        setPriorityR1("");

        const dev = deviceList.find((d) => d.name === deviceName);
        if (dev) {
            await fetchIpsForDevice(dev.id, "R1");
        }
    }

    // Device R2
    async function handleSelectDeviceR2(deviceName: string) {
        setDeviceR2(deviceName);
        setIpListR2([]);
        setSelectedIpR2("");
        setPriorityR2("");

        const dev = deviceList.find((d) => d.name === deviceName);
        if (dev) {
            await fetchIpsForDevice(dev.id, "R2");
        }
    }

    // Cargar IPs para un device R1 o R2
    async function fetchIpsForDevice(deviceId: string, which: "R1" | "R2") {
        try {
            setIsFetching(true);
            const token = await getAccessTokenSilently();
            const resp = await fetchInterfaces(token, deviceId);
            if (which === "R1") {
                setIpListR1(resp.data);
            } else {
                setIpListR2(resp.data);
            }
        } catch (error) {
            console.error("Error fetching IP list =>", error);
        } finally {
            setIsFetching(false);
        }
    }

    // Guardar
    async function handleSave() {
        const sub = subscriptionList.find((s) => s.purchaseOrden === selectedSubscription);
        if (!sub) {
            console.error("No subscription selected");
            return;
        }
        const dev1 = deviceList.find((d) => d.name === deviceR1);
        const dev2 = deviceList.find((d) => d.name === deviceR2);
        if (!dev1 || !dev2) {
            console.error("Device R1 or R2 is missing");
            return;
        }

        const payload = {
            suscriptionId: String(sub.id),
            name: clusterName,
            description: clusterDescription,
            ctxr: [
                {
                    uuid: dev1.id,
                    localpref: priorityR1,
                    ip: selectedIpR1,
                },
                {
                    uuid: dev2.id,
                    localpref: priorityR2,
                    ip: selectedIpR2,
                },
            ],
        };

        try {
            setIsFetching(true);
            const token = await getAccessTokenSilently();
            await createCluster(token, payload);
            onClose();
            onSuccess();
        } catch (error) {
            console.error("Error creating cluster =>", error);
        } finally {
            setIsFetching(false);
        }
    }

    // Validación
    function isFormValid() {
        return (
            selectedSubscription.trim() !== "" &&
            clusterName.trim() !== "" &&
            deviceR1.trim() !== "" &&
            selectedIpR1.trim() !== "" &&
            priorityR1.trim() !== "" &&
            deviceR2.trim() !== "" &&
            selectedIpR2.trim() !== "" &&
            priorityR2.trim() !== ""
        );
    }

    // 1) Calcula la lista filtrada para R1 (excluye lo que R2 tiene seleccionado)
    function getFilteredDeviceListForR1(): string[] {
        return deviceList
            .filter((d) => d.name !== deviceR2)  // si en R2 está seleccionado X, no lo mostramos en R1
            .map((d) => d.name);
    }

    // 2) Calcula la lista filtrada para R2 (excluye lo que R1 tiene seleccionado)
    function getFilteredDeviceListForR2(): string[] {
        return deviceList
            .filter((d) => d.name !== deviceR1)  // si en R1 está seleccionado Y, no lo mostramos en R2
            .map((d) => d.name);
    }

    return (
        <>
            <GenericModal
                isOpen={isOpen}
                onClose={onClose}
                title="Create Cluster"
                size="max-w-4xl"
            >
                <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                        <label className="text-sm font-medium">
                            Subscription <span className="text-red-500">*</span>
                        </label>
                        <GenericDropdown
                            items={subscriptionList.map((s) => s.purchaseOrden)}
                            value={selectedSubscription}
                            onSelect={handleSelectSubscription}
                            defaultLabel="Select a Subscription"
                            widthClass="w-full"
                            maxWidthClass="max-w-[400px]"
                        />
                    </div>

                    <GenericInput
                        label={
                            <span>
                                Name <span className="text-red-500">*</span>
                            </span>
                        }
                        value={clusterName}
                        onChange={setClusterName}
                    />

                    <GenericInput
                        label="Description"
                        value={clusterDescription}
                        onChange={setClusterDescription}
                    />

                    {/* CTX-R 1 */}
                    <div>
                        <label className="text-sm font-medium">
                            CTX-R 1 <span className="text-red-500">*</span>
                        </label>
                        <GenericDropdown
                            items={getFilteredDeviceListForR1()}
                            value={deviceR1}
                            onSelect={handleSelectDeviceR1}
                            defaultLabel="Select a Device"
                            widthClass="w-full"
                            maxWidthClass="max-w-[400px]"
                        />
                    </div>

                    <div>
                        <label className="text-sm font-medium">
                            IP for HA <span className="text-red-500">*</span>
                        </label>
                        <GenericDropdown
                            items={ipListR1.map((ip) => ip.value)}
                            value={selectedIpR1}
                            onSelect={setSelectedIpR1}
                            defaultLabel="Select an IP"
                            widthClass="w-full"
                            maxWidthClass="max-w-[400px]"
                        />
                    </div>

                    <div>
                        <label className="text-sm font-medium flex items-center gap-1">
                            Priority <span className="text-red-500">*</span>
                            <IconQuestion
                                className="w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-700"
                                onClick={() => setShowPriorityR1Info(true)}
                            />
                        </label>
                        <GenericInput value={priorityR1} onChange={setPriorityR1} />
                    </div>

                    {/* CTX-R 2 (filtrado) */}
                    <div>
                        <label className="text-sm font-medium">
                            CTX-R 2 <span className="text-red-500">*</span>
                        </label>
                        <GenericDropdown
                            items={getFilteredDeviceListForR2()}
                            value={deviceR2}
                            onSelect={handleSelectDeviceR2}
                            defaultLabel="Select a Device"
                            widthClass="w-full"
                            maxWidthClass="max-w-[400px]"
                        />
                    </div>

                    <div>
                        <label className="text-sm font-medium">
                            IP for HA <span className="text-red-500">*</span>
                        </label>
                        <GenericDropdown
                            items={ipListR2.map((ip) => ip.value)}
                            value={selectedIpR2}
                            onSelect={setSelectedIpR2}
                            defaultLabel="Select an IP"
                            widthClass="w-full"
                            maxWidthClass="max-w-[400px]"
                        />
                    </div>

                    <div>
                        <label className="text-sm font-medium flex items-center gap-1">
                            Priority <span className="text-red-500">*</span>
                            <IconQuestion
                                className="w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-700"
                                onClick={() => setShowPriorityR2Info(true)}
                            />
                        </label>
                        <GenericInput value={priorityR2} onChange={setPriorityR2} />
                    </div>
                </div>

                <div className="flex justify-end gap-2">
                    <GenericButton
                        variant="secondary"
                        label="Cancel"
                        onClick={onClose}
                    />
                    <GenericButton
                        variant="brand"
                        label="Save"
                        onClick={handleSave}
                        disabled={!isFormValid() || isFetching}
                    />
                </div>
            </GenericModal>

            {overlaySpinner}

            {showPriorityR1Info && (
                <GenericAlert
                    isOpen={true}
                    onClose={() => setShowPriorityR1Info(false)}
                    onConfirm={() => setShowPriorityR1Info(false)}
                    title="Priority Explanation"
                    description={`This field determines the order in which announcements are made. A higher value indicates a higher priority for initiating announcements.
When comparing two entries, the one with the greater value serves as the primary channel, while the one with the lesser value acts as a fallback.`}
                    variant="warning"
                    confirmLabel="Got it!"
                />
            )}

            {showPriorityR2Info && (
                <GenericAlert
                    isOpen={true}
                    onClose={() => setShowPriorityR2Info(false)}
                    onConfirm={() => setShowPriorityR2Info(false)}
                    title="Priority Explanation"
                    description={`This field determines the order in which announcements are made. A higher value indicates a higher priority for initiating announcements.
When comparing two entries, the one with the greater value serves as the primary channel, while the one with the lesser value acts as a fallback.`}
                    variant="warning"
                    confirmLabel="Got it!"
                />
            )}
        </>
    );
}
